import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, map, findIndex, isEmpty, reduce, values } from 'lodash'

import VNBlueSwitch from '../VNBlueSwitch'
import SidebarModal from '../SidebarModal'
import BackChevron from '../BackChevron'
import Button from '../Button'

import { ReactComponent as Unchecked } from '../../assets/icons/fill-unchecked.svg'
import { ReactComponent as Checkmark } from '../../assets/icons/fill-checkmark.svg'

import styles from './SplitTabsModal.module.scss'
import { setCardAuthStepOne, SetOfflineDialog, setStartATabModal, setTabPaymentMethodModal } from '../../VNDialogs/ActionCreators'
import { useSplitTab } from '../../VNSplitTabs/hooks/useSplitTab'
import { generateSplitOrderNumber, invoiceNumber, orderUuidFromOrderNumber } from '../../utils/orderNumbers'
import DropdownSelector from '../DropdownSelector'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { fetchRemoteOrderTotalSucceeded } from '../../actions/orderTotalRemote'
import { getPaymentNames, getTabNamesByKeyword } from '../../utils/orderUtils'
import { getReopenedTabPayments } from '../../VNTenders/Selectors'
import { getAllOrders } from '../../selectors/order'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import usePriceAdjustment from '../../VNItems/hooks/usePriceAdjustment'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { getDeviceSerial } from '../../utils/deviceData'
import { getTransactionBrandingConfig } from '../../utils/buildSettings'
import { setCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/ActionCreators'
import { CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import { bridge_sendExecuteAuthRequest } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { getCurrentPaymentFlow, getTransactionResult } from '../../VNCustomerFacingDisplay/Selectors'
import { getCardAuthStepTwo } from '../../VNDialogs/Selectors'
import { orderDetailsRequested, selectOrder } from '../../actions/order'
import { centsToDollars } from '../../utils/formatters'
import { setBridgePaymentMethod } from '../../actions/peripheral'
import { bridgePaymentMethods } from '../../reducers/peripheral'
import { getRequirePaymentForTabs } from '../../selectors/menus'
import { APPLICATION_STRINGS } from '../../strings'
import { getItemsForCurrentMenu } from '../../selectors/items'
import { PAYMENT_TYPES_INTERNAL } from '../../utils/paymentTypes'

const SplitTabsModal = ({ order, edit, onClose, className, ...props }) => {
  const dispatch = useDispatch()
  const isMountedRef = useRef(false)
  const classNames = cn(styles.splitTabsModal, className)
  const baseItems = get(order, 'itemModels', [])
  const history = useHistory()
  const networkAvailable = getNetworkAvailableValue()
  const cfdMode = useSelector(getDeviceMode) === MODES.POS
  const cfdAuthResponse = useSelector((state) => getTransactionResult(state))
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))
  const authStepTwoModal = useSelector((state) => getCardAuthStepTwo(state))
  const itemsForCurrentMenu = useSelector(getItemsForCurrentMenu)

  const items = useMemo(() => {
    const charityItemExists = baseItems[baseItems.length - 1]?.type === 'DonationLineItem'
    const donationLineItem = {
      name: 'Donation',
      priceInCents: order.charityAmountInCents,
      defaultPriceInCents: order.charityAmountInCents,
      modifiers: [],
      price: centsToDollars(order.charityAmountInCents),
      type: 'DonationLineItem',
      quantity: 1,
      modifiersString: '',
    }

    if (!order?.wasCreatedInStadium && order?.charityAmountInCents > 0 && !charityItemExists) {
      return [...baseItems, donationLineItem]
    }
    return baseItems
  }, [baseItems, order?.wasCreatedInStadium, order?.charityAmountInCents])

  const totalInCents = order?.amountInCents ?? 0
  const [selectedItems, setSelectedItems] = useState([])
  const [splitTabAmount, setSplitTabAmount] = useState(0)
  const [tempOrderDetails, setTempOrderDetails] = useState(null)
  const { getSplitOrderTotal, initiateTab } = useSplitTab()
  const { retotalOfflineOrder } = usePriceAdjustment()
  const allOrders = useSelector(getAllOrders)
  const reopenedTabPayments = useSelector((state) => getReopenedTabPayments(state, order.uuid))
  const requirePaymentForTabs = useSelector((state) => getRequirePaymentForTabs(state))
  const editMode = useMemo(() => edit, [edit])

  const pastDayTime = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

  const allTabOrders = useMemo(() => {
    return values(allOrders).filter((order) => order.isTab && Date.now() - new Date(order?.createdAt) < pastDayTime)
  }, [allOrders, pastDayTime])

  const modifiedItems = useMemo(() => {
    return Object.values(
      items.reduce((acc, item) => {
        const key = `${item?.id}-${item.variant?.productSku || ''}-${item.openPriceInCents || item.priceInCents}-${item.notes || ''}-${item.modifiersString || ''}` // Group by id, variant, open price, notes and modifiers to handle same item with different open prices, variants, notes or modifiers

        if (!acc[key]) {
          acc[key] = {
            id: item?.id,
            name: item?.name,
            priceInCents: item?.priceInCents,
            price: item?.price,
            quantity: 0,
            originalItems: [],
          }
        }

        acc[key].originalItems.push(item)
        acc[key].quantity = acc[key].originalItems.length

        return acc
      }, {}),
    )
  }, [items])

  const [selectedItemQuantities, setSelectedItemQuantities] = useState(Array(modifiedItems?.length).fill(1))

  const getReopenPayments = useCallback(() => {
    return reopenedTabPayments.map((payment) => {
      let description = payment.shortDescription

      if (payment.userAttributes?.cardholderName) {
        description = `${description} • ${payment.userAttributes.cardholderName}`
      }

      return [PAYMENT_TYPES_INTERNAL.REOPEN_SAVED_CARD, description, false, null, payment]
    })
  }, [reopenedTabPayments])

  const getPaymentFromReOpen = useCallback(
    (paymentId) => {
      return reopenedTabPayments.get(paymentId)
    },
    [reopenedTabPayments],
  )

  // this is wrong, we also need the quantity
  const isEntireOrder =
    modifiedItems.length === selectedItems.length &&
    modifiedItems.every((item, index) => item.quantity === selectedItemQuantities[index])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    calculateSplitTabAmount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, selectedItemQuantities])

  useEffect(() => {
    const parseCfdAuthResponse = cfdAuthResponse?.toJS()

    if (currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION) return
    if (authStepTwoModal?.get('open')) return
    if (!cfdMode) return
    if (isEmpty(parseCfdAuthResponse)) return

    dispatch(
      setStartATabModal({
        open: true,
        onSaveClick: (tabName) => {
          initiateTab(tempOrderDetails, tabName, {}, parseCfdAuthResponse, null, onClose, true)
          setTempOrderDetails(null)
          dispatch(selectOrder(tempOrderDetails?.oldOrder?.uuid))
          if (!tempOrderDetails?.offline) {
            dispatch(orderDetailsRequested(tempOrderDetails?.oldOrder?.uuid))
          }
        },
        initialInputValue: cfdAuthResponse?.get('cardholderName')?.trim(),
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfdAuthResponse, currentPaymentFlow, authStepTwoModal])

  const toggleItem = (index, item) => {
    //once refunded... don't let the user change the toggle
    if (item.refund) return
    const isChecked = selectedItems.includes(index)
    if (isChecked) {
      selectedItems.splice(
        findIndex(selectedItems, (itemIndex) => itemIndex === index),
        1,
      )
      setSelectedItems([...selectedItems])
    } else {
      setSelectedItems([index, ...selectedItems])
    }
  }

  const adjustItemQuantity = (index, newQuantity) => {
    selectedItemQuantities[index] = newQuantity
    setSelectedItemQuantities([...selectedItemQuantities])
  }

  const calculateSplitTabAmount = async () => {
    const items = map(selectedItems, (itemIndex) => {
      const item = modifiedItems[itemIndex]
      return item?.originalItems?.slice(0, selectedItemQuantities[itemIndex]).map((i) => i)
    }).flat()

    let splitAmount = 0
    items.forEach((item) => {
      item?.modifiers?.forEach((mod) => {
        splitAmount = splitAmount + mod.defaultPriceInCents
      })
      splitAmount = splitAmount + item.priceInCents
    })

    setSplitTabAmount(splitAmount)
  }

  const onSelectEntireOrder = () => {
    if (!editMode) return
    if (isEntireOrder) {
      setSelectedItems([])
      setSelectedItemQuantities(Array(modifiedItems?.length).fill(1))
      return
    }

    setSelectedItems(modifiedItems.map((item, index) => index))
    setSelectedItemQuantities(modifiedItems.map((item) => item?.quantity))
  }

  const getCheckboxStyle = (refund, isItemChecked, isModifierChecked) => {
    return isModifierChecked || (isItemChecked && !isModifierChecked) ? (
      <Checkmark
        className={(isItemChecked && isModifierChecked) || refund ? styles.checkBoxIconUnSelectable : styles.checkBoxIcon}
      />
    ) : (
      <Unchecked className={styles.checkBoxIcon} />
    )
  }

  const renderItems = (item, index) => {
    const isItemChecked = selectedItems.includes(index) || item.refund
    const showQuantityDropdown = item.quantity > 1

    return (
      <div key={index} className={styles.selectableItemContainer}>
        <div
          className={styles.selectableItem}
          key={`${item.name}-${index}`}
          onClick={() => item?.quantity === 1 && toggleItem(index, item)}
        >
          <div className={cn([styles.item, isItemChecked && styles.selected])}>
            <div onClick={() => item?.quantity > 1 && toggleItem(index, item)} className={styles.column}>
              {getCheckboxStyle(item.refund, isItemChecked)}
            </div>
            <div className={styles.column}>
              <span
                className={cn(styles.name, { [styles.shorter]: showQuantityDropdown })}
              >{`(${item.quantity}) ${item.name} - ${centsToDollars(item.priceInCents)}`}</span>
            </div>
            {showQuantityDropdown && (
              <DropdownSelector
                disabled={!isItemChecked}
                defaultValue={`${selectedItemQuantities[index]}` || '1'}
                autocompleteOptions={[...Array(item.quantity).keys()].map((i) => (i + 1).toString())}
                onAutocompleteChange={(inputValue) => {
                  adjustItemQuantity(index, parseInt(inputValue))
                }}
                classes={{ root: styles.rootDropdown }}
                customAutocompleteStyles={{
                  width: '70px !important',
                  paddingTop: '0px !important',
                  paddingRight: '8px !important',
                }}
                customTextFieldStyles={{
                  paddingLeft: '0px !important',
                  height: '100% !important',
                }}
                customInputLabelStyles={{
                  paddingLeft: '0px !important',
                  paddingRight: '0px !important',
                }}
                autocomplete={{
                  style: { marginRight: 8 },
                }}
                sx={{ width: '70px' }}
                getOptionLabel={(option) => option?.toString()}
              />
            )}
          </div>
        </div>

        {map(item.originalItems?.[0]?.modifiers, (modifier, modIndex) => {
          return (
            <div className={styles.selectableItem} key={`${modifier?.name}-${index}-${modIndex}`}>
              <div className={cn([styles.item, isItemChecked && styles.selected])}>
                <div className={styles.modColumn}></div>
                <div className={styles.column}>
                  <span
                    className={styles.name}
                  >{`(1) ${modifier.name} - $${(modifier.defaultPriceInCents / 100).toFixed(2)}`}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const handleAddPaymentLater = (orderDetails, tabName) => {
    dispatch(setTabPaymentMethodModal({ open: false }))
    dispatch(
      setStartATabModal({
        open: true,
        onSaveClick: (newTabName) => initiateTab(orderDetails, newTabName, null, [], null, onClose, false, true, false),
        initialInputValue: tabName,
      }),
    )
  }

  // TODO: move these to testable functions in hook/utils and add unit tests
  // https://github.com/venuenext/venuenext-pos/issues/5179
  const handlePreAuthorizedCard = (orderDetails, tabName, payment = null) => {
    const getOriginalPayments = () => {
      if (orderDetails?.oldOrder?.reopened) {
        return [getPaymentFromReOpen(payment?.[4]?.paymentId)]
      }
      return orderDetails?.oldOrder?.payments || []
    }
    const isCardReaderOffline = getOriginalPayments()?.[0]?.userAttributes?.isOfflineApproval

    if (isCardReaderOffline) {
      dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.SPLIT_TABS_OFFLINE_AUTH_ERROR))
      return
    }

    let oldPayments = orderDetails?.oldOrder?.payments || []

    if (order?.reopened && payment) {
      oldPayments = [getPaymentFromReOpen(payment?.[4]?.paymentId)]
    }

    dispatch(setTabPaymentMethodModal({ open: false }))
    dispatch(
      setStartATabModal({
        open: true,
        onSaveClick: (newTabName) =>
          initiateTab(
            orderDetails,
            newTabName,
            orderDetails?.oldOrder?.userAttributes,
            oldPayments,
            null,
            onClose,
            false,
            false,
            true,
          ),
        initialInputValue: tabName,
      }),
    )
  }

  // this requires to go to card auth screen then navigate back to orders screen
  const handleAuthorizePaymentRequest = (orderDetails) => {
    dispatch(setTabPaymentMethodModal({ open: false }))
    dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeAuthRequest))
    let total = orderDetails?.amount
    if (!orderDetails?.offline) {
      total = orderDetails?.remoteOrderTotal?.totalAmountInCents
    }

    if (cfdMode) {
      const displayAmount = centsToDollars(total)

      let paymentData = {
        amount: displayAmount,
        tipAmount: 0,
        hRef: invoiceNumber(orderDetails?.orderNumber),
        orderNumber: orderDetails?.orderNumber,
        uuid: orderDetails?.uuid,
        deviceSerial: getDeviceSerial(),
        ...getTransactionBrandingConfig(),
      }

      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION, true))
      dispatch(setCardAuthStepOne({ open: true, amount: displayAmount }))
      bridge_sendExecuteAuthRequest(paymentData)
      setTempOrderDetails(orderDetails)
      return
    }
    history.push('/card-reader-auth', {
      redirectPath: '/orders',
      orderDetails,
      splitTab: true,
      authTabs: true,
      total: total,
      orderNumber: orderDetails?.orderNumber,
      tenderAmountInCents: total,
      orderUuid: orderDetails?.uuid,
    })
  }

  const onSplitHandler = async () => {
    const itemIds = map(selectedItems, (itemIndex) => {
      const item = modifiedItems[itemIndex]
      return item?.originalItems?.slice(0, selectedItemQuantities[itemIndex]).map((i) => {
        // This mapping is required because CartItemViewModel expects certain props to be present on the item
        i.taxable = itemsForCurrentMenu?.byId[i.id].taxable
        i.taxClassUuid = itemsForCurrentMenu?.byId[i.id].taxClassUuid

        i.variants = [i.variant] || []
        if (!i.originalPriceInCents) {
          i.defaultPriceInCents = i.priceInCents
          i.openPriceInCents = null
          return i
        }

        i.defaultPriceInCents = i.originalPriceInCents
        if (i.priceInCents !== i.defaultPriceInCents) {
          i.openPriceInCents = i.priceInCents
        }

        return i
      })
    }).flat()

    const orderNumber = generateSplitOrderNumber(order?.orderNumber, 3)
    const isOfflineOrder = networkAvailable === false && !order?.wasCreatedInStadium

    let newOrderDetails = {
      orderNumber,
      uuid: orderUuidFromOrderNumber(orderNumber),
      itemIds,
      oldOrder: order,
      offline: isOfflineOrder,
      tabName: `${order?.tabName || order?.userName} ${getTabNamesByKeyword(allTabOrders, order?.tabName || order?.userName).length + 1}`,
    }

    if (isOfflineOrder) {
      const { amountInCents } = retotalOfflineOrder(itemIds)
      newOrderDetails.amount = amountInCents
      newOrderDetails.offline = true

      // this only need to be done for offline orders
      const unselectedItems = reduce(
        modifiedItems,
        (acc, item, itemIndex) => {
          const isItemChecked = selectedItems.includes(itemIndex)
          if (!isItemChecked) {
            return acc.concat(
              item?.originalItems?.map((i) => {
                i.taxable = itemsForCurrentMenu?.byId[i.id].taxable
                i.taxClassUuid = itemsForCurrentMenu?.byId[i.id].taxClassUuid
                i.variants = [i.variant] || []
                if (!i.originalPriceInCents) {
                  i.defaultPriceInCents = i.priceInCents
                  i.openPriceInCents = null
                  return i
                }

                if (i.priceInCents !== i.defaultPriceInCents) {
                  i.openPriceInCents = i.priceInCents
                }

                return i
              }),
            )
          }
          if (selectedItemQuantities[itemIndex] && isItemChecked < item.quantity) {
            return acc.concat(
              item?.originalItems?.slice(selectedItemQuantities[itemIndex], item.quantity).map((i) => {
                i.variants = [i.variant] || []
                i.taxable = itemsForCurrentMenu?.byId[i.id].taxable
                i.taxClassUuid = itemsForCurrentMenu?.byId[i.id].taxClassUuid

                if (!i.originalPriceInCents) {
                  i.defaultPriceInCents = i.priceInCents
                  i.openPriceInCents = null
                  return i
                }

                i.defaultPriceInCents = i.originalPriceInCents
                if (i.priceInCents !== i.defaultPriceInCents) {
                  i.openPriceInCents = i.priceInCents
                }

                return i
              }),
            )
          }

          return acc
        },
        [],
      )

      newOrderDetails.unselectedItems = unselectedItems
    } else {
      const remoteOrderTotal = await getSplitOrderTotal(itemIds)
      dispatch(fetchRemoteOrderTotalSucceeded(remoteOrderTotal))

      newOrderDetails.remoteOrderTotal = remoteOrderTotal
      newOrderDetails.amount = remoteOrderTotal.totalAmountInCents
    }

    let preauthCardName = order?.payments?.length > 0 && getPaymentNames(order, reopenedTabPayments)

    if (order?.reopened) {
      const preAuthedCards = getReopenPayments().toJS()
      if (Object.keys(reopenedTabPayments).length > 0) {
        preauthCardName = []
        // eslint-disable-next-line no-unused-vars
        for (const [_, value] of Object.entries(preAuthedCards)) {
          preauthCardName.push(value)
        }
      }
      newOrderDetails.preAuthedCard = preauthCardName
    }

    dispatch(
      setTabPaymentMethodModal({
        open: true,
        handleAddPaymentLater,
        handleAuthorizePaymentRequest,
        handlePreAuthorizedCard,
        shouldShowAddPaymentLater: !requirePaymentForTabs,
        preAuthorizedCardName: preauthCardName,
        newOrderDetails,
        authNewText: true,
      }),
    )
  }

  return (
    <SidebarModal
      show
      title="Split Tab"
      onClose={onClose}
      leftAccessory={<BackChevron onClick={onClose} />}
      className={classNames}
      {...props}
    >
      <div className={styles.splitTabsModal}>
        <div className={styles.orderNumber}>{`Order # ${order.orderNumber}`}</div>
        <div className={styles.splitTabsContainer}>
          <div className={styles.selectItems}>Select items</div>
          <div className={styles.selectableItem} key={'Entire Order'} onClick={onSelectEntireOrder}>
            <div className={cn([styles.item, styles.entireOrder])}>
              <div className={styles.column}>
                <span className={styles.name}>{`Entire Order - ${centsToDollars(totalInCents)}`}</span>
              </div>
              <div className={cn([styles.colomn, styles.entireOrderToggle])}>
                <VNBlueSwitch disableRipple checked={isEntireOrder} />
              </div>
            </div>
          </div>
          {map(modifiedItems, renderItems)}
        </div>
        <div className={styles.gradient}></div>
        <div className={styles.submitContainer}>
          {editMode && (
            <Button className={styles.submitButton} disabled={isEmpty(selectedItems)} onClick={onSplitHandler}>
              Split Tab ({centsToDollars(splitTabAmount)})
            </Button>
          )}
        </div>
      </div>
    </SidebarModal>
  )
}

SplitTabsModal.defaultProps = {
  order: {},
  onClose: () => {},
  edit: true,
}

SplitTabsModal.propTypes = {
  order: PropTypes.object,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
}

export default SplitTabsModal
